import React from "react";
import { Form as BaseForm } from "react-aria-components";

import { WagtailForm, reverse } from "@reactivated";

import { CSRF_HEADER, loadCSRFToken } from "@thelabnyc/thelabui/src/utils/api";

import { ButtonPrimary } from "../Clickables";
import Widget from "./fields";

const submitForm = async (
    pageId: number,
    formData: FormData,
): Promise<Response> => {
    const submitURL = reverse("cms:submit-form", {
        page_id: pageId,
    });
    const csrfToken = await loadCSRFToken();
    // nosemgrep: CWE-918
    const response = await fetch(submitURL, {
        method: "POST",
        headers: [
            ["Accept", "application/json"],
            [CSRF_HEADER, csrfToken],
        ],
        body: formData,
    });
    return response;
};

type Errors = {
    [name: string]: string[];
};

export function Form({
    pageId,
    form,
    defaultIsSubmitted = false,
    children,
    ...props
}: React.ComponentProps<typeof BaseForm> & {
    defaultIsSubmitted?: boolean;
    pageId: number;
    form?: WagtailForm;
}) {
    const [validationErrors, setValidationErrors] = React.useState<Errors>();
    const [submitted, setSubmitted] = React.useState(defaultIsSubmitted);
    const [loading, setLoading] = React.useState(false);

    const onSubmit = React.useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            setLoading(true);
            const response = await submitForm(pageId, data);
            setLoading(false);
            if (response.ok) {
                setSubmitted(true);
            } else {
                setValidationErrors(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    await response.json(),
                );
            }
        },
        [pageId],
    );

    if (submitted || !form) return children;

    return (
        <BaseForm
            {...props}
            method="post"
            validationErrors={validationErrors}
            onSubmit={onSubmit}
        >
            <input type="hidden" name="form_name" value={form.name} />
            {form.iterator.map((fieldName) => (
                <Widget key={fieldName} {...form.fields[fieldName]} />
            ))}

            <ButtonPrimary loading={loading} type="submit" icon="arrow">
                Submit
            </ButtonPrimary>
        </BaseForm>
    );
}
